
import { useEffect, useRef, useState } from "react";
import { Alert, Button, Container, OffcanvasBody, OffcanvasHeader } from "reactstrap";
import styles from "./style.module.scss"
import cx from "classnames";
import DefaultOffCanvas, { DefaultOffCanvasProps } from "../DefaultOffCanvas";
import { CloseProviderInvoiceRecibePubSub, CurrencyIdPubSub, OpenProviderInvoiceRecibePubSub, OpenProviderInvoiceRecibePubSubPayload } from "@utils/pubsubs/PubSub";
import LoadingScreen from "@components/content/LoadingScreen";
import { SuscriptionDataType } from "@/api/SuscriptionsApi";
import { DynamicFormRef } from "@mavichpe/dynamic-forms";
import { ClientStatusRowData, InterestConfigType, normalizeClientStatusRow } from "@utils/DocumentStatus";
import { formatMoneyValue } from "@utils/Currency";
import { showAlert } from "@utils/Alert";
import { DocumentsEntryType } from "@components/dynamic/DocumentStatusList";
import { ProviderRecibeApi, ProviderRecibeDataType } from "@/api/ProviderRecibeApi";
import { ProviderInvoiceApi, ProviderInvoiceDataType } from "@/api/ProviderInvoiceApi";
import ProviderInvoicesRecibeForm from "@components/forms/ProviderInvoicesRecibeForm";
import { BuyInvoiceApi, BuyInvoiceDataType } from "@/api/BuyInvoiceApi";
import { ProviderInvoicePaymentApi, ProviderInvoicePaymentDataType } from "@/api/ProviderInvoicePaymentApi";
import { BuyInvoicePaymentApi, BuyInvoicePaymentDataType } from "@/api/BuyInvoicePaymentApi";
import { ProviderInvoicesRecibeDataType } from "@/formTypes/ProviderInvoicesRecibeFormDataType";
import { INVOICE_PAYMENT_ACTIVE_STATUS } from "@utils/Constants";
import { AbonoDocumentKind, buildAbonoDocumentKey, buildProviderRecibeMotivo, parseAbonoDocumentKey } from "@utils/ProviderRecibeAbono";
import { omitProperties, toNumberFormat } from "@utils/Utils";

type ProviderRecibeOffCanvasProps = {
    suscriptionData: SuscriptionDataType,
    onRecibeCreated?: (generatedRecibe: ProviderRecibeDataType) => void
} & DefaultOffCanvasProps

const ProviderRecibeOffCanvas = (props: ProviderRecibeOffCanvasProps) => {
    const { btnColor = "primary", className, onRecibeCreated } = props;
    const [isOpen, setOpen] = useState(false);
    const [isLoading, setLoading] = useState(false);
    const [generatedInterest, setGeneratedInterest] = useState<number>(0);
    const [recibeDate, setRecibeDate] = useState<string>("");
    const formRef = useRef<DynamicFormRef>(null);
    const [normalizedDocumentsData, setNormalizedDocumentsData] = useState<ClientStatusRowData[]>([])
    const [defaultData, setDefaultData] = useState<ProviderInvoicesRecibeDataType>({} as ProviderInvoicesRecibeDataType)
    const [recibeLoadData, setRecibeLoadData] = useState<OpenProviderInvoiceRecibePubSubPayload>({} as OpenProviderInvoiceRecibePubSubPayload)

    useEffect(() => {
        const unsubscribe = OpenProviderInvoiceRecibePubSub.subscribe(openOffCanvas);
        const unsubscribeClose = CloseProviderInvoiceRecibePubSub.subscribe(closeOffCanvas);
        return () => {
            unsubscribe();
            unsubscribeClose();
        };
    }, []);

    useEffect(() => {
        if (recibeDate && recibeLoadData.selectedDocuments.length > 0) {
            normalizeData()
        }
    }, [recibeDate, recibeLoadData]);

    const normalizeData = async () => {
        const normalizedDocumentsData: ClientStatusRowData[] = await Promise.all(recibeLoadData.selectedDocuments.map((r) => normalizeClientStatusRow(recibeDate, r, {} as InterestConfigType)));
        const totalGeneratedInterest = normalizedDocumentsData.reduce((a, d) => {
            return a + Number(d.interest)
        }, Number(0))
        setGeneratedInterest(totalGeneratedInterest);
        setNormalizedDocumentsData(normalizedDocumentsData);
    }

    const closeOffCanvas = async () => {
        setOpen(false);
    }
    const openOffCanvas = async (payload: OpenProviderInvoiceRecibePubSubPayload) => {
        const { openOffCanvas = false } = payload;
        setOpen(openOffCanvas);
        if (openOffCanvas) {
            const defaultData: ProviderInvoicesRecibeDataType = {
                created: payload.recibeDate,
                total: payload.recibeTotal
            }
            setRecibeDate(payload.recibeDate)
            setRecibeLoadData(payload)
            setDefaultData(defaultData)
        }
    }

    const toogleOffCanvas = (newState: boolean) => {
        setOpen(newState);
        if (newState == false) {
            onCanvasClosed();
        }
    }

    const onCanvasClosed = () => {
        setOpen(false);
    }

    const onOffCanvasOpened = () => {
        CurrencyIdPubSub.publish({ currencyId: `${recibeLoadData.recibeCurrency.id}` })

        let formDefaultData = { ...defaultData }
        const payableDocuments = (recibeLoadData.selectedDocuments || []).filter(d => d.kind == "providerInvoice" || d.kind == "buyInvoice");
        if (payableDocuments.length > 0) {
            const lastDocument = payableDocuments[payableDocuments.length - 1];
            formDefaultData = {
                ...formDefaultData,
                factura_abono: buildAbonoDocumentKey(lastDocument.kind, `${lastDocument.data.id}`)
            }
        }
        formRef.current?.resetFormValue(formDefaultData)
    }

    const resolveProviderId = (documents: DocumentsEntryType[]): string | undefined => {
        for (const doc of documents) {
            if (doc.kind === "providerInvoice" || doc.kind === "buyInvoice") {
                const providerId = doc.data.providerId;
                if (providerId) {
                    return `${providerId}`;
                }
            }
        }
        return undefined;
    };

    const onProviderRecibeSubmit = async (formData?: ProviderInvoicesRecibeDataType) => {
        if (formData) {
            const { selectedDocuments = [], recibeTotal } = recibeLoadData
            const recibeMonto = Number(formData?.total)
            const isPartialPayment = Number(toNumberFormat(recibeTotal)) > Number(toNumberFormat(recibeMonto))

            let abonoDocumentData: ClientStatusRowData = {} as ClientStatusRowData;
            let abonoDocumentKind: AbonoDocumentKind = "providerInvoice";
            if (isPartialPayment) {
                const abonoTarget = parseAbonoDocumentKey(formData?.factura_abono)
                abonoDocumentKind = abonoTarget.kind
                abonoDocumentData = normalizedDocumentsData.find(d => d.id == abonoTarget.id) || {} as ClientStatusRowData

                if (!abonoDocumentData?.id) {
                    showAlert("danger", "Seleccione la factura a la que se le aplicara el abono")
                    return
                }

                const pendingAmount = Number(toNumberFormat(recibeTotal - recibeMonto))
                if (Number(toNumberFormat(abonoDocumentData?.saldo || 0)) < pendingAmount) {
                    showAlert("danger", `El monto del abono supero el monto del saldo de la Factura #${abonoDocumentData?.numero}`);
                    return
                }
            }

            const fullPayNumeros = normalizedDocumentsData
                .filter(d => d.id != abonoDocumentData?.id)
                .map(d => d.numero)
            const motivo = buildProviderRecibeMotivo(fullPayNumeros, isPartialPayment ? abonoDocumentData?.numero : undefined)

            setLoading(true)
            try {
                const providerId = resolveProviderId(selectedDocuments)
                if (!providerId) {
                    showAlert("danger", "No fue posible determinar el proveedor para este recibo")
                    return
                }

                const recibe: ProviderRecibeDataType = omitProperties({
                    ...formData,
                    abono: recibeMonto,
                    total: Number(toNumberFormat(recibeTotal)),
                    providerId: providerId,
                    motivo: motivo,
                    created: formData?.created
                }, ["factura_abono"])

                const generatedRecibe = await ProviderRecibeApi.create(recibe);
                await markDocumentAsPayed(isPartialPayment, abonoDocumentKind, abonoDocumentData, generatedRecibe, recibeMonto, selectedDocuments);

                showAlert("success", isPartialPayment
                    ? `Se ha guardado el recibo del provedore Exitosamente y se aplico el abono a la factura #${abonoDocumentData?.numero}`
                    : `Se ha guardado el recibo del provedore Exitosamente`);

                if (onRecibeCreated) {
                    onRecibeCreated(generatedRecibe);
                }
            } catch (err) {
                if (err instanceof Error) {
                    showAlert("danger", err.message);
                }
            } finally {
                setLoading(false)
            }
        }
    }

    const markDocumentAsPayed = async (
        isPartialPayment: boolean,
        abonoDocumentKind: AbonoDocumentKind,
        abonoDocumentData: ClientStatusRowData,
        generatedRecibe: ProviderRecibeDataType,
        recibeMonto: number,
        selectedDocuments: DocumentsEntryType[]
    ) => {
        const relativeDataPromises = [];

        if (isPartialPayment) {
            const fullPayDocuments = selectedDocuments.filter(d => `${d.data.id}` != abonoDocumentData.id);
            const fullPayDocumentTotal = normalizedDocumentsData
                .filter(d => d.id != abonoDocumentData.id)
                .reduce((a, d) => a + Number(toNumberFormat(d.saldo)), Number(0))

            relativeDataPromises.push(createAbonoPayment(abonoDocumentKind, abonoDocumentData, generatedRecibe, Number(toNumberFormat(recibeMonto - fullPayDocumentTotal))));
            relativeDataPromises.push(...generateMarkDocumentAsPayedPromise(fullPayDocuments, generatedRecibe));
        } else {
            relativeDataPromises.push(...generateMarkDocumentAsPayedPromise(selectedDocuments, generatedRecibe));
        }

        await Promise.all(relativeDataPromises);
    }

    /**
     * Registra el abono sobre el documento elegido. Ese documento no se marca `pagada`
     * ni se liga al recibo: sigue pendiente y su saldo baja porque `normalizeProvider` /
     * `normalizeBuyInvoice` restan los abonos activos.
     */
    const createAbonoPayment = (
        abonoDocumentKind: AbonoDocumentKind,
        abonoDocumentData: ClientStatusRowData,
        generatedRecibe: ProviderRecibeDataType,
        monto: number
    ) => {
        const paymentData = {
            providerRecibeId: generatedRecibe.id,
            monto,
            recibo: `${generatedRecibe.numero_recibo}`,
            reference: generatedRecibe.reference,
            digitosTarjeta: generatedRecibe.digitosTarjeta,
            numAprobacion: generatedRecibe.numAprobacion,
            creditOperatorId: generatedRecibe.creditOperatorId,
            bankAccountId: generatedRecibe.bankAccountId,
            currencyId: generatedRecibe.currencyId,
            exchange_rate: generatedRecibe.exchange_rate,
            created: generatedRecibe.created,
            estado: INVOICE_PAYMENT_ACTIVE_STATUS,
            descripcion: `Abono por recibo #${generatedRecibe.numero_recibo}`,
        };

        if (abonoDocumentKind == "buyInvoice") {
            const payment: BuyInvoicePaymentDataType = omitProperties({
                ...paymentData,
                formaPago: `${generatedRecibe.forma_pago}`,
                buyInvoiceId: abonoDocumentData.id,
            }, [], true);
            return BuyInvoicePaymentApi.create(payment);
        }

        const payment: ProviderInvoicePaymentDataType = omitProperties({
            ...paymentData,
            forma_pago: `${generatedRecibe.forma_pago}`,
            providerInvoiceId: abonoDocumentData.id,
        }, [], true);
        return ProviderInvoicePaymentApi.create(payment);
    }

    const generateMarkDocumentAsPayedPromise = (selectedDocuments: DocumentsEntryType[], generatedRecibe: ProviderRecibeDataType) => {
        return selectedDocuments.map(d => {
            if (d.kind == "providerInvoice") {
                const documentUpdate: ProviderInvoiceDataType = {
                    id: d.data.id,
                    providerRecibeId: generatedRecibe.id,
                    pagada: true,
                    status: "pagada",
                    providerId: d.data.providerId
                }

                return ProviderInvoiceApi.update(`${documentUpdate.id}`, documentUpdate)
            }
            if (d.kind == "buyInvoice") {
                const documentUpdate: BuyInvoiceDataType = {
                    id: d.data.id,
                    providerRecibeId: generatedRecibe.id,
                    pagada: true,
                    status: "pagada",
                    providerId: d.data.providerId,
                    buyInvoiceType: "Buy"
                }

                return BuyInvoiceApi.update(`${documentUpdate.id}`, documentUpdate)
            }
        })
    }

    return (
        <>
            {props.children && (<Button
                color={btnColor}
                type="button"
                className={cx([className, styles.button])}
                onClick={() => toogleOffCanvas(!isOpen)}
            >
                {props.children}
            </Button>
            )}
            <DefaultOffCanvas
                className={styles.offCanvas}
                direction="end"
                isOpen={isOpen}
                onOpened={onOffCanvasOpened}
                onClosed={onCanvasClosed}
                toggle={() => toogleOffCanvas(!isOpen)}
            >

                <OffcanvasHeader className={styles.offCanvasHeader} toggle={onCanvasClosed}>
                    Desea Crear Recibo
                </OffcanvasHeader>
                <OffcanvasBody>
                    <Container>
                        <p>
                            Por favor ingrese la informacion de la forma de pago.
                        </p>
                        <ProviderInvoicesRecibeForm
                            ref={formRef}
                            onRecibeDateChange={(value) => setRecibeDate(value)}
                            onProviderInvoiceRecibeSubmit={onProviderRecibeSubmit}
                            recibeTotal={recibeLoadData.recibeTotal}
                            selectedDocuments={recibeLoadData.selectedDocuments}
                            recibeCurrency={recibeLoadData.recibeCurrency}
                        />
                        {generatedInterest > 0 && (<Alert className="mt-4" color="warning">
                            <b>Advertencia!</b> La facturas seleccionas tiene un total de intereses pendiente por <b>{formatMoneyValue(generatedInterest, recibeLoadData.recibeCurrency.currency_id || 1)}</b>
                            por lo cual al cancelar estas facturas se creara una liquidacion por el monto anteriorment mencionado.
                        </Alert>)}
                    </Container>
                </OffcanvasBody>
            </DefaultOffCanvas >
            <LoadingScreen isLoading={isLoading} />
        </>
    );
}
export default ProviderRecibeOffCanvas;
